import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Project, ProjectPhoto, ProjectSettingsModeEnum } from '@/app/api';
import { PropsWithClassname } from '@/app/types';

import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import FinishedProjectSummary from '@/app/components/FinishedProjectSummary/FinishedProjectSummary';
import ProjectProgress from '@/app/components/ProjectProgress/ProjectProgress';
import ProjectStatistics from '@/app/components/ProjectStatistics/ProjectStatistics';
import ProjectFinishedCoverMedia from '@/app/components/ProjectFinishedCoverMedia/ProjectFinishedCoverMedia';

interface ProjectFinishedWidgetProps extends PropsWithClassname {
  project: Project;
  coverImage: ProjectPhoto;
  onReadMoreClick: () => void;
}

const ProjectFinishedWidget = ({ project, className, onReadMoreClick, coverImage }: ProjectFinishedWidgetProps) => {
  const { t } = useTranslation();

  const isProjectProgressVisible = useMemo(() => {
    if (typeof project.statistics?.targetAmountInCents !== 'number') {
      return false;
    }

    if (typeof project.statistics?.currentAmountInCents !== 'number') {
      return false;
    }

    return true;
  }, [project]);

  const isFinishedProjectSummaryVisible = useMemo(() => {
    if (typeof project.statistics?.targetAmountInCents === 'number') {
      return false;
    }

    if (typeof project.statistics?.currentAmountInCents !== 'number') {
      return false;
    }

    return true;
  }, [project]);

  const projectStatusLabel = useMemo(() => {
    if (!project.settings?.mode) {
      return;
    }

    if ([ProjectSettingsModeEnum.DonioPlus as string].includes(project.settings.mode)) {
      if (!project.state?.actualSuccess) {
        return t('components.ProjectFinishedWidget.projectClosed');
      }

      return t('components.ProjectFinishedWidget.projectSuccessful');
    }

    if (!project.state?.actualSuccess) {
      return t('components.ProjectFinishedWidget.charityCollectionClosed');
    }

    return t('components.ProjectFinishedWidget.charityCollectionSuccessful');
  }, [project.settings, project.state, t]);

  const readMoreButtonText = useMemo(() => {
    if (project.settings?.mode === ProjectSettingsModeEnum.DonioPlus) {
      return t('components.ProjectFinishedWidget.readMoreAboutProject');
    }

    return t('components.ProjectFinishedWidget.readMoreAboutCharityCollection');
  }, [project.settings, t]);

  return (
    <div className={className}>
      <div className="flex flex-col lg:flex-row lg:justify-between lg:pb-[48px] lg:gap-[120px] mb-[32px] lg:mb-0">
        <div className="w-full">
          <div className="font-medium text-[10px] lg:text-[12px] tracking-[3px] lg:tracking-[4.5px] leading-[24px] uppercase">
            {projectStatusLabel}
          </div>
          <h1 className="w-full block text-[32px] lg:text-[48px] leading-[32px] lg:leading-[48px] font-medium text-neutral-dark mb-[24px] lg:mb-[40px]">
            {project.content?.currentName}
          </h1>
        </div>

        {isProjectProgressVisible && (
          <ProjectProgress
            totalInCents={project.statistics?.targetAmountInCents ?? undefined}
            currentValueInCents={project.statistics?.currentAmountInCents!}
            matching={project.donationMatching ?? undefined}
            milestones={project.milestones}
            endsAt={project.timestamps?.finishedAt ?? project.timestamps?.scheduledEnding ?? undefined}
            donationsCount={project.statistics?.donationsCount}
            className="w-full lg:max-w-[384px]"
          />
        )}
        {isFinishedProjectSummaryVisible && (
          <FinishedProjectSummary
            matching={project.donationMatching ?? undefined}
            donationsCount={project.statistics?.donationsCount}
            currentValueInCents={project.statistics?.currentAmountInCents!}
            className="w-full lg:max-w-[384px]"
          />
        )}
      </div>
      <div className="flex flex-col gap-[16px] md:gap-0 lg:flex-row lg:justify-between">
        <ProjectFinishedCoverMedia
          coverImage={coverImage}
          coverImageAlt={project.content.currentName}
          mainVideoSlug={project.content.texts.mainVideo ?? undefined}
        />
        <div className="flex flex-col align-center">
          {!!project.statistics && !!project.timestamps && !!project.settings?.mode && (
            <ProjectStatistics
              className="w-full lg:w-[384px]"
              statistics={project.statistics}
              projectMode={project.settings?.mode}
              timestamps={project.timestamps}
            />
          )}
          <Button
            style={ButtonStyle.SUCCESS_OUTLINED}
            size={ButtonSize.LARGE}
            tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onReadMoreClick }}
            className="self-start px-[22px] py-[18px]"
          >
            {readMoreButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectFinishedWidget;
